/**
 * The main component that renders single TabComponent
 * instances.
 */
import { DynamicTabsDirective } from '../../directive/dynamic-tabs/dynamictab.component';
import { Component, ContentChildren, QueryList, AfterContentInit, ViewChild, ComponentFactoryResolver, ViewContainerRef, NgModule, Compiler, NO_ERRORS_SCHEMA, OnInit, OnDestroy } from '@angular/core';

import { TabComponent } from '../tab/tab.component';
import { Router, ActivatedRoute, NavigationEnd, CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot, RoutesRecognized, Route } from '@angular/router';
import { Observable, Subscription } from 'rxjs/Rx';
import { SessionService } from 'app/shared/service/session.service';
import { AdminComponent } from 'app/admin/admin.component';
import { AgentComponent } from 'app/agent/agent.component';

@Component({
  selector: 'app-dynamic-tabs',
  template: `
    <div id="tab-holder">
      <div *ngFor="let tab of tabs" (click)="selectTab(tab)" [class.tab-active]="tab.active" class="fixed-tab tab">
        <span>{{tab.tabTitle}}</span>
      </div>
      <!-- dynamic tabs -->
      <div *ngFor="let tab of dynamicTabs" (click)="selectTab(tab)" [class.tab-active]="tab.active" class="tab">
        <span>{{tab.tabTitle}} <span class="tab-close" *ngIf="tab.isCloseable" (click)="closeTab(tab)">x</span></span>
      </div>
    </div>
    <ng-content></ng-content>
    <ng-template appDynamicTabs #container></ng-template>
  `,
  styles: []
})
export class TabsComponent implements AfterContentInit, OnDestroy {

  dynamicTabs: TabComponent[] = [];

  private readonly MAX_TABS = 10;

  urls: string[] = [];

  @ContentChildren(TabComponent)
  tabs: QueryList<TabComponent>;

  @ViewChild(DynamicTabsDirective)
  dynamicTabPlaceholder: DynamicTabsDirective;

  private lastComponent: any;
  private lastUrl: string;

  private routerEvent: Subscription;

  ac: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private _componentFactoryResolver: ComponentFactoryResolver,
    private sessionService: SessionService,
    private adc: AdminComponent,
    private agc: AgentComponent) {

    this.ac = this.sessionService.getAdminLogin() ? adc : agc;

    this.router.onSameUrlNavigation = 'reload';

    this.routerEvent = this.router.events.subscribe((event) => {

      if (event instanceof RoutesRecognized) {
        let root = this.router.routerState.snapshot.root.children;
        for (let child = root[0]; root.length !== 0; child = root[0]) {
          if (child.children.length === 0) {
            break;
          }
          root = child.children;
        }
        if (root[0].component === this.lastComponent) {
          delete (<any>root[0]).routeConfig;
        }
      }

      if (event instanceof NavigationEnd) {
        let root = this.router.routerState.root.children;
        for (let child = root[0]; root.length !== 0; child = root[0]) {
          if (child.children.length === 0) {
            break;
          }
          root = child.children;
        }
        const tabTitle = (<any>root[0].data)._value.title;
        let closeable = (<any>root[0].data)._value.closeable;
        let multiple = (<any>root[0].data)._value.multiple;
        let tabable = (<any>root[0].data)._value.tabable;
        let static_url = (<any>root[0].data)._value.url;
        if (multiple === undefined) {
          multiple = true;
        }
        if (closeable === undefined) {
          closeable = true;
        }
        if (tabable === undefined) {
          tabable = true;
        }
        if (!static_url) {
          static_url = event.urlAfterRedirects;
        }
        if (tabable) {
          this.lastComponent = root[0].component;
          if (!multiple) {
            const isStaticExist = this.tabs.filter(tab => {
              if (tab.tabTitle === tabTitle) {
                return true;
              }
            });
            const isDynamicExist = this.dynamicTabs.filter(tab => {
              if (tab.tabTitle === tabTitle) {
                return true;
              }
            });

            const isExist = isDynamicExist.concat(isStaticExist);
            if (isExist.length > 0) {
              if (tabTitle === 'Customer') {
                this.ac.alert('Customer can not be opened at that same time. Please close all taps first and retry.');
              }
              this.fosucTab(isExist[0], null);
              return;
            }
          }
          if (this.urls.indexOf(event.url) > -1) {
            this.fosucTab(null, event.url);
          } else if (this.urls.indexOf(event.urlAfterRedirects) > -1) {
            this.fosucTab(null, event.urlAfterRedirects);
          } else {
            if (this.dynamicTabs.length >= this.MAX_TABS) {
              this.ac.alert('Only upto 10 Tabs supported. Please close unused tabs');
              return;
            }
            this.lastUrl = static_url;
            this.openTab(tabTitle, root[0].component, static_url, closeable);
          }
        }
      }
    });
  }

  // contentChildren are set
  ngAfterContentInit() {
    // get all active tabs
    const activeTabs = this.tabs.filter((tab) => tab.active);
    const urls = this.tabs.map(tab => tab.url).filter(url => url ? true : false);
    this.urls = urls;
    // if there is no active tab set, activate the first
    if (activeTabs.length === 0) {
      const tab = this.dynamicTabs.length > 0 ? this.dynamicTabs[0] : this.tabs.first;
      this.selectTab(tab);
    }
  }

  ngOnDestroy(): void {
    this.routerEvent.unsubscribe();
  }

  openTab(title: string, template, url, isCloseable = false) {

    const componentFactory = this._componentFactoryResolver.resolveComponentFactory(TabComponent);
    // fetch the view container reference from our anchor directive
    const viewContainerRef = this.dynamicTabPlaceholder.viewContainer;

    // create a component instance
    const componentRef = viewContainerRef.createComponent(componentFactory);

    // set the according properties on our component instance
    const instance: TabComponent = componentRef.instance as TabComponent;
    instance.tabTitle = title;
    instance.template = template;
    instance.url = url;
    instance.isCloseable = isCloseable;
    instance.close.subscribe(this.closeTab.bind(this));

    // remember the dynamic component for rendering the
    // tab navigation headers
    this.dynamicTabs.push(componentRef.instance as TabComponent);
    this.urls.push(url);
    // set it active
    this.selectTab(this.dynamicTabs[this.dynamicTabs.length - 1]);
  }

  selectTab(tab: TabComponent) {
    // deactivate all tabs
    if (tab) {
      if (!tab.active) {
        this.tabs.toArray().forEach((_tab) => _tab.active = false);
        this.dynamicTabs.forEach((_tab) => _tab.active = false);
        // activate the tab the user has clicked on.
        tab.active = true;
      }
    }
  }

  fosucTab(tab?: TabComponent, url?: string, isDynamic = true) {

    const tabs = isDynamic ? this.dynamicTabs : this.tabs;
    if (url) {
      for (let i = 0; i < this.urls.length; i++) {
        if (this.urls[i] === url) {
          this.selectTab(tabs[i - 1]);
          break;
        }
      }
    } else if (tab) {
      //      for (let i = 0; i < tabs.length; i++) {
      this.selectTab(tabs.find((_tab) => {
        if (_tab === tab) {
          return true;
        }
        return false;
      }));
      //        break;
      //      }
    }
  }


  closeTab(tab: TabComponent, url?: string) {
    for (let i = 0; i < this.dynamicTabs.length; i++) {
      if (this.dynamicTabs[i] === tab) {
        // remove the tab from our array
        this.dynamicTabs.splice(i, 1);
        this.urls.splice(i + this.tabs.length, 1);
        // destroy our dynamically created component again
        const viewContainerRef = this.dynamicTabPlaceholder.viewContainer;
        // let viewContainerRef = this.dynamicTabPlaceholder;
        viewContainerRef.remove(i);

        // set tab index to the last tab if closed one was activated.
        if (tab.active) {
          if (this.dynamicTabs.length) {
            this.selectTab(this.dynamicTabs[this.dynamicTabs.length - 1]);
          } else {
            this.selectTab(this.tabs.first);
          }
        }
        break;
      }
    }
    if (this.dynamicTabs.length === 0) {
      this.router.navigate([this.urls[0]]);
    }
  }

  closeActiveTab() {
    const activeTabs = this.dynamicTabs.filter((tab) => tab.active);
    if (activeTabs.length > 0) {
      // close the 1st active tab (should only be one at a time)
      this.closeTab(activeTabs[0]);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/tab/tabs/tabs.component.ts